import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type Difficulty, type QuestionStatus } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminQuestionsList() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  if (!me.isLoading && !canManageQuestions(me.data)) return <Navigate to="/admin" replace />;
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [difficulty, setDifficulty] = useState<Difficulty | "">("");
  const [status, setStatus] = useState<QuestionStatus | "">("");
  const [page, setPage] = useState(1);

  const categories = useQuery({ queryKey: ["categories"], queryFn: () => adminApi.listCategories() });
  const list = useQuery({
    queryKey: ["questions", search, categoryId, difficulty, status, page],
    queryFn: () => adminApi.listQuestions({
      search: search || undefined,
      categoryId: categoryId || undefined,
      difficulty: (difficulty || undefined) as Difficulty | undefined,
      status: (status || undefined) as QuestionStatus | undefined,
      page, pageSize: 25,
    }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteQuestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions"] }),
  });

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize)) : 1;

  return (
    <main className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-brand-ink">Question bank</h1>
        <div className="flex gap-2">
          <Link to="/admin/questions/import" className="btn-secondary">Import from Excel</Link>
          <Link to="/admin/questions/new" className="btn-primary">New question</Link>
        </div>
      </div>

      <div className="card mb-4 flex flex-wrap gap-3 p-4">
        <input className="field-input max-w-xs" placeholder="Search question text"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="field-input max-w-xs" value={categoryId}
          onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}>
          <option value="">All categories</option>
          {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="field-input max-w-[9rem]" value={difficulty}
          onChange={(e) => { setDifficulty(e.target.value as Difficulty | ""); setPage(1); }}>
          <option value="">Any difficulty</option>
          <option value="Easy">Easy</option>
          <option value="Medium">Medium</option>
          <option value="Hard">Hard</option>
        </select>
        <select className="field-input max-w-[9rem]" value={status}
          onChange={(e) => { setStatus(e.target.value as QuestionStatus | ""); setPage(1); }}>
          <option value="">Any status</option>
          <option value="Active">Active</option>
          <option value="Draft">Draft</option>
          <option value="Archived">Archived</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-brand-ink">
              <tr>
                {["Question", "Category", "Difficulty", "Status", "Options", "Updated", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((q) => (
                <tr key={q.id} className="border-t border-brand-ink/10">
                  <td className="max-w-md truncate px-3 py-2">
                    {q.externalId && <span className="mr-2 rounded bg-brand-light px-1.5 py-0.5 text-xs">{q.externalId}</span>}
                    {q.text}
                  </td>
                  <td className="px-3 py-2">{q.categoryName}</td>
                  <td className="px-3 py-2">{q.difficulty}</td>
                  <td className="px-3 py-2">{q.status}</td>
                  <td className="px-3 py-2">{q.optionCount}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-brand-ink/60">
                    {new Date(q.updatedAt).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                    <Link to={`/admin/questions/${q.id}`} className="mr-3 text-brand hover:underline">Edit</Link>
                    <button className="text-brand hover:underline"
                      onClick={() => { if (confirm("Delete this question?")) remove.mutate(q.id); }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {list.data?.items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-brand-ink/60">No questions match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {list.data && list.data.totalCount > list.data.pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-brand-ink/60">
            Page {list.data.page} of {totalPages} · {list.data.totalCount} total
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}
    </main>
  );
}
